import React, { useState, useEffect } from 'react';
import { ArrowLeft, Users, Target, Shield, Zap, Brain, Star, Quote, CheckCircle, Menu, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth, AuthModal } from '../auth/AuthComponents';
import { useNavigate } from 'react-router-dom';
import DocxsAiLogo from '../shared/DocxsAiLogo';

const MobileAboutPage = ({ setShowAuthModal: setShowAuthModalProp }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [showAuthModalLocal, setShowAuthModalLocal] = useState(false);
  const setShowAuthModal = setShowAuthModalProp || setShowAuthModalLocal;
  const showAuthModal = setShowAuthModalProp ? undefined : showAuthModalLocal;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedSection, setExpandedSection] = useState('about');
  const [currentBenefitSlide, setCurrentBenefitSlide] = useState(0);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const sections = [
    { id: 'about', title: 'About Us', icon: <Target className="w-6 h-6" /> },
    { id: 'benefits', title: 'Why Choose Us', icon: <Zap className="w-6 h-6" /> },
    // { id: 'testimonials', title: 'What Clients Say', icon: <Star className="w-6 h-6" /> },
  ];

  const values = [
    {
      icon: <Shield className="w-10 h-10" />,
      title: "Contract Analysis & Revamp",
      description: "Upload any contract and get a structured review in minutes. Get flagged risks, unclear clauses, and practical suggestions that help you negotiate with confidence."
    },
    {
      icon: <Target className="w-10 h-10" />,
      title: "SOP Analysis & Revamp", 
      description: "Strengthen your statement of purpose with clear feedback on structure, tone, and fit. Know exactly what to improve before you submit."
    },
    {
      icon: <Users className="w-10 h-10" />,
      title: "CV Analysis & Revamp",
      description: "Sharpen your CV for stronger first impressions. Get better wording, clearer impact, and tighter alignment with the roles you are targeting."
    }
  ];

  const benefits = [
    {
      icon: <Zap className="w-12 h-12" />,
      title: "Lightning Fast",
      description: "99% faster than traditional methods",
      stat: "30 seconds"
    },
    {
      icon: <Brain className="w-12 h-12" />,
      title: "AI-Powered",
      description: "Advanced machine learning precision",
      stat: "99.7% accuracy"
    },
    {
      icon: <Shield className="w-12 h-12" />,
      title: "Secure",
      description: "Enterprise-grade protection",
      stat: "Zero breaches"
    }
  ];

  const testimonials = [
    // {
    //   name: "Dr. Sarah Mitchell",
    //   role: "Senior Researcher",
    //   // company: "Stanford University",
    //   image: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=400&h=400&fit=crop&crop=face",
    //   rating: 5,
    //   text: "DocxsAi transformed how we analyze contracts. What used to take days now takes minutes with remarkable accuracy."
    // },
    // {
    //   name: "Michael Chen", 
    //   role: "HR Director",
    //   // company: "TechCorp",
    //   image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop&crop=face",
    //   rating: 5,
    //   text: "We identify top candidates 3x faster while reducing bias. Game-changing technology for our recruitment process."
    // }
    {
      name: "Tobi Adeyemi",
      role: "Legal Counsel", 
      // company: "GlobalLaw Partners",
      //image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=400&fit=crop&crop=face",
      rating: 5,
      text: "Contract review is lightning fast. The risk assessment has helped catch issues before they become expensive problems."
    }
  ];

  // Auto-rotate testimonials
  useEffect(() => {
    if (expandedSection === 'testimonials') {
      const interval = setInterval(() => {
        setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [expandedSection, testimonials.length]);

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star 
        key={i} 
        className={`w-4 h-4 ${i < rating ? 'text-yellow-400 fill-current' : 'text-[var(--color-text-muted)]'}`} 
      />
    ));
  };

  const navLinks = [
    { name: 'Home', action: () => navigate('/home'), current: false },
    { name: 'About', href: '#', current: true },
    { name: 'Pricing', action: () => navigate('/pricing'), current: false },
  ];

  useEffect(() => {
    if (expandedSection === 'benefits') {
      setCurrentBenefitSlide(0);
    }
  }, [expandedSection]);

  const renderAboutSection = () => (
    <div className="p-6 space-y-8">
      {/* Hero Section */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-[var(--color-text)] mb-4 leading-tight">
          Optimizing Document{" "}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--color-gradient-from)] to-[var(--color-gradient-to)]">
            Processing with AI
          </span>
        </h1>
        <p className="text-base text-[var(--color-text-muted)] leading-relaxed">
          DocxsAi helps professionals transform complex documents into clear, actionable insights using AI, enabling faster, smarter decision-making with confidence.
        </p>
      </div>

      {/* Values - Mobile Stacked */}
      <div className="space-y-4">
        <h2 className="text-xl font-bold text-[var(--color-text)] text-center mb-6">Our Services</h2>
        {values.map((value, index) => (
          <div 
            key={index}
            className="elevated-card backdrop-blur-sm rounded-2xl p-6 hover:bg-[var(--color-surface-muted)] transition-all"
          >
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-16 h-16 rounded-xl bg-gradient-to-r from-[var(--color-primary-soft)] to-[var(--color-primary-soft)] flex items-center justify-center">
                <div className="text-[var(--color-primary)]">
                  {value.icon}
                </div>
              </div>
              <div className="flex-grow">
                <h3 className="text-lg font-bold text-[var(--color-text)] mb-2">
                  {value.title}
                </h3>
                <p className="text-[var(--color-text-muted)] text-sm leading-relaxed">
                  {value.description}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const nextBenefitSlide = () => setCurrentBenefitSlide((prev) => (prev + 1) % benefits.length);
  const prevBenefitSlide = () => setCurrentBenefitSlide((prev) => (prev - 1 + benefits.length) % benefits.length);

  const renderBenefitsSection = () => (
    <div className="p-6 space-y-6">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-[var(--color-text)] mb-4 leading-tight">
          Why Choose{" "}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--color-gradient-from)] to-[var(--color-gradient-to)]">
            DocxsAi
          </span>
          ?
        </h1>
        <p className="text-base text-[var(--color-text-muted)] leading-relaxed">
          DocxsAi is your complete AI document workspace for generating, improving, reviewing, and securely signing documents with confidence.
        </p>
      </div>

      {/* Benefits Carousel */}
      <div className="relative px-2">
        <div className="overflow-hidden">
          <div
            className="flex transition-transform duration-500 ease-in-out"
            style={{ transform: `translateX(-${currentBenefitSlide * 100}%)` }}
          >
            {benefits.map((benefit, index) => (
              <div key={index} className="w-full flex-shrink-0 px-2">
                <div className="elevated-card backdrop-blur-sm rounded-2xl p-6">
                  <div className="text-center">
                    <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-gradient-to-r from-[var(--color-primary-soft)] to-[var(--color-primary-soft)] flex items-center justify-center">
                      <div className="text-[var(--color-primary)]">
                        {benefit.icon}
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-[var(--color-text)] mb-3">
                      {benefit.title}
                    </h3>
                    <p className="text-[var(--color-text-muted)] mb-4 leading-relaxed">
                      {benefit.description}
                    </p>
                    <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-[var(--color-primary-soft)] to-[var(--color-primary-soft)] rounded-full">
                      <span className="text-sm font-medium text-[var(--color-primary)]">{benefit.stat}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <button
          onClick={prevBenefitSlide}
          className="absolute left-0 top-1/2 -translate-y-1/2 p-2 bg-[var(--color-surface-muted)]/80 hover:bg-gray-700/80 rounded-full transition-all z-10"
          aria-label="Previous benefit"
        >
          <ChevronLeft className="w-5 h-5 text-[var(--color-text)]" />
        </button>
        <button
          onClick={nextBenefitSlide}
          className="absolute right-0 top-1/2 -translate-y-1/2 p-2 bg-[var(--color-surface-muted)]/80 hover:bg-gray-700/80 rounded-full transition-all z-10"
          aria-label="Next benefit"
        >
          <ChevronRight className="w-5 h-5 text-[var(--color-text)]" />
        </button>
        <div className="flex justify-center space-x-2 mt-4">
          {benefits.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentBenefitSlide(index)}
              className={`w-2.5 h-2.5 rounded-full transition-all ${
                currentBenefitSlide === index
                  ? 'bg-[var(--color-primary)]'
                  : 'bg-gray-600 hover:bg-gray-500'
              }`}
              aria-label={`Go to benefit ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Quick Features */}
      <div className="space-y-3">
        {["Instant Analysis", "99.7% Accuracy", "24/7 Available"].map((feature, index) => (
          <div key={index} className="flex items-center justify-center space-x-3 p-4 elevated-card rounded-xl">
            <CheckCircle className="w-5 h-5 text-green-400" />
            <span className="text-[var(--color-text)] font-medium">{feature}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const renderTestimonialsSection = () => (
    <div className="p-6 space-y-6">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-[var(--color-text)] mb-4 leading-tight">
          Trusted by{" "}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--color-gradient-from)] to-[var(--color-gradient-to)]">
            Industry Leaders
          </span>
        </h1>
        <p className="text-base text-[var(--color-text-muted)] leading-relaxed">
          See what professionals have to say about their DocxsAi experience.
        </p>
      </div>

      {/* Current Testimonial */}
      <div className="elevated-card backdrop-blur-sm rounded-2xl p-6">
        <div className="text-center mb-6">
          <Quote className="w-10 h-10 text-[var(--color-primary)] mx-auto mb-4" />
          <p className="text-base text-[var(--color-text-secondary)] leading-relaxed italic mb-6">
            "{testimonials[currentTestimonial].text}"
          </p>
        </div>
        
        <div className="flex items-center space-x-4 mb-6">
          {/* <img 
            src={testimonials[currentTestimonial].image} 
            alt={testimonials[currentTestimonial].name}
            className="w-16 h-16 rounded-xl object-cover"
          /> */}
          <div className="flex-grow">
            <h4 className="text-[var(--color-text)] font-bold">{testimonials[currentTestimonial].name}</h4>
            <p className="text-[var(--color-primary)] font-medium text-sm">{testimonials[currentTestimonial].role}</p>
            <div className="flex space-x-1">
              {renderStars(testimonials[currentTestimonial].rating)}
            </div>
            <p className="text-[var(--color-text-muted)] text-xs">{testimonials[currentTestimonial].company}</p>
          </div>

        </div>
        
        {/* Testimonial indicators */}
        <div className="flex justify-center space-x-3">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentTestimonial(index)}
              className={`w-3 h-3 rounded-full transition-all ${
                currentTestimonial === index 
                  ? 'bg-[var(--color-primary)]' 
                  : 'bg-gray-600'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );

  const renderSectionContent = () => {
    switch (expandedSection) {
      case 'about': return renderAboutSection();
      case 'benefits': return renderBenefitsSection();
      // case 'testimonials': return renderTestimonialsSection();
      default: return renderAboutSection();
    }
  };

  // Handler for any CTA (if you add one in the future)
  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      setShowAuthModal(true);
    }
  };

  return (
    <>
      <div className="min-h-screen public-page-glow relative overflow-hidden">
        {/* Mobile Navigation Header */}
        <nav className="bg-[var(--color-surface)] backdrop-blur-md border-b border-[var(--color-border)] sticky top-0 z-30">
          <div className="px-4 py-3">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <DocxsAiLogo className="h-8 w-auto" />
              </div>
              {/* Mobile Hamburger Menu - Always show hamburger on mobile */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 rounded-lg text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-surface-muted)] transition-colors"
                aria-label="Toggle mobile menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="border-t border-[var(--color-border)] bg-[var(--color-surface)]">
              <div className="px-4 pt-3 pb-3 space-y-3">
                {navLinks.map((link) => (
                  link.action ? (
                    <button
                      key={link.name}
                      onClick={() => {
                        link.action();
                        setMobileMenuOpen(false);
                      }}
                      className={`w-full text-left block px-4 py-3 rounded-md text-base font-medium transition-colors ${
                        link.current 
                          ? 'text-[var(--color-text)] bg-[var(--color-surface-muted)]' 
                          : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-text)]'
                      }`}
                    >
                      {link.name}
                    </button>
                  ) : (
                    <a
                      key={link.name}
                      href={link.href}
                      className={`block px-4 py-3 rounded-md text-base font-medium transition-colors ${
                        link.current 
                          ? 'text-[var(--color-text)] bg-[var(--color-surface-muted)]' 
                          : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-text)]'
                      }`}
                    >
                      {link.name}
                    </a>
                  )
                ))}
              </div>
              <div className="pt-4 pb-6 border-t border-[var(--color-border)] px-4">
                {isAuthenticated ? (
                  <button
                    onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }}
                    className="brand-gradient-btn w-full px-4 py-3 rounded-xl"
                  >
                    Dashboard
                  </button>
                ) : (
                  <button
                    onClick={() => { setShowAuthModal(true); setMobileMenuOpen(false); }}
                    className="brand-gradient-btn w-full px-4 py-3 rounded-xl"
                  >
                    Get Started
                  </button>
                )}
              </div>
            </div>
          )}
        </nav>

        {/* Section Tabs */}
        <div className="bg-[var(--color-surface)] backdrop-blur-sm border-b border-[var(--color-border)]/50 sticky top-16 z-20">
          <div className="flex overflow-x-auto scrollbar-hide">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setExpandedSection(section.id)}
                className={`flex-shrink-0 flex items-center space-x-2 px-4 py-3 font-medium transition-colors border-b-2 ${
                  expandedSection === section.id
                    ? 'text-[var(--color-text)] border-[var(--color-primary)] bg-[var(--color-surface-muted)]/50'
                    : 'text-[var(--color-text-muted)] border-transparent hover:text-[var(--color-text)] hover:bg-[var(--color-surface-muted)]/30'
                }`}
              >
                {section.icon}
                <span className="text-sm">{section.title}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Floating Background Elements */}
        <div className="absolute top-20 left-4 w-32 h-32 bg-[var(--color-primary-soft)] rounded-full blur-2xl"></div>
        <div className="absolute top-40 right-4 w-40 h-40 bg-gradient-to-br from-[var(--color-gradient-from)]/10 to-[var(--color-gradient-to)]/10 rounded-full blur-2xl"></div>

        {/* Main Content */}
        <main className="relative z-10">
          {renderSectionContent()}
        </main>

        {/* Footer - Remove direct usage */}
      </div>
      
      <AuthModal 
        isOpen={setShowAuthModalProp ? false : showAuthModalLocal} 
        onClose={() => setShowAuthModalLocal(false)} 
      />
    </>
  );
};

export default MobileAboutPage; 